import { useQuery } from '@tanstack/react-query';
import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorPage from '../../components/ErrorPage';
import UpdateProjectDetailsModal from '../../components/modal/UpdateProjectDetailsModal';
import DeleteAction from '../../FatchAction/DeleteAction';
import { FiCalendar, FiCode, FiEdit3, FiExternalLink, FiLayers, FiServer, FiTrash2 } from 'react-icons/fi';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const RichProjectContent = ({ content, fallback }) => (
  content ? (
    <div className="project-details-rich-text">
      <ReactQuill value={content} readOnly theme="bubble" modules={{ toolbar: false }} />
    </div>
  ) : (
    <p>{fallback}</p>
  )
);

const ProjectDetails = () => {
  const { id } = useParams();
  const [projectDetailsData, setProjectDetailsData] = useState({});
  const {
    data: projectDetails = {},
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ["projectDetails", id],
    queryFn: async () => {
      try {
        const res = await fetch(`${process.env.REACT_APP_SERVER_URL}/projectdetails/${id}`, {
          method: "GET",
          headers: {
            authorization: `${localStorage.getItem("token")}`,
          },
        });
        if (!res.ok) {
          throw new Error("Network response was not ok");
        }
        const data = await res?.json();
        return data;
      } catch (error) {
        toast.error(`Failed to fetch reviews: ${error?.message}`);
      }
    },
  });

  if (isLoading) {
    return <LoadingSpinner />;
  };

  

  if (error) {
    return <ErrorPage />;
  }

  const {
    _id,
    project,
    projecttype,
    statingTime,
    enddingTime,
    feature,
    src: projectDetailsSrc,
    featureDiscription,
    responsiveDesign,
    technologiesUsed,
  } = projectDetails?.data || {};

  const { src, demo, code, server, createdAt } = project || {};

  const formattedDate = new Date(createdAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  }) || "";

  const projectdetailsModal = () => {
    document.getElementById("projectdetails_modal").showModal();
    setProjectDetailsData({
      projecttype,
      statingTime,
      enddingTime,
      feature,
      src: projectDetailsSrc,
      featureDiscription,
      responsiveDesign,
      technologiesUsed,
      _id
    });
  }

  const handelDeleteProjectDetails = (id) => {
    if (id) {
      DeleteAction(`${process.env.REACT_APP_SERVER_URL}/projectdetails/${id}`, refetch);
    }
  }

  return (
    <>
      {!isLoading && (
        <div className="project-details-page">
          <div className="project-details-header">
            <div>
              <span className="dashboard-account-eyebrow">Project workspace / detail view</span>
              <h1>Project details</h1>
              <p>Review the visual direction, timeline, and implementation notes for this project.</p>
            </div>
            <div className="project-details-header-actions">
              <button disabled={!projectDetails?.data} onClick={() => projectdetailsModal()} className="project-details-edit-button">
                <FiEdit3 /> Update details
              </button>
              <button disabled={!projectDetails?.data} onClick={() => handelDeleteProjectDetails(_id)} className="project-details-delete-button">
                <FiTrash2 /> Delete
              </button>
              <UpdateProjectDetailsModal projectDetailsData={projectDetailsData} refetch={refetch} />
            </div>
          </div>

          <article className="project-details-card">
            <div className="project-details-gallery">
              <div className="project-details-image project-details-image-main">
                <img src={src || "https://cdn.vectorstock.com/i/500p/65/30/default-image-icon-missing-picture-page-vector-40546530.jpg"} alt="Project preview" />
                <span>Live project</span>
              </div>
              <div className="project-details-image project-details-image-secondary">
                <img src={projectDetailsSrc || "https://cdn.vectorstock.com/i/500p/65/30/default-image-icon-missing-picture-page-vector-40546530.jpg"} alt="Project detail preview" />
                <span>Detail view</span>
              </div>
            </div>

            <div className="project-details-body">
              <div className="project-details-title-row">
                <div>
                  <span className="project-details-status"><FiLayers /> Featured project</span>
                  <h2>{projecttype || "Web development project"}</h2>
                </div>
                <div className="project-details-date"><FiCalendar /> {formattedDate || "No date"}</div>
              </div>

              <div className="project-details-meta-grid">
                <div><span>Project type</span><strong>{projecttype || "Not specified"}</strong></div>
                <div><span>Feature</span><strong>{feature || "Not specified"}</strong></div>
                <div><span>Starting date</span><strong>{statingTime || "Not specified"}</strong></div>
                <div><span>Ending date</span><strong>{enddingTime || "Not specified"}</strong></div>
              </div>

              <div className="project-details-content-grid">
                <section><span className="project-details-section-label">Feature description</span><RichProjectContent content={featureDiscription} fallback="No feature description has been added yet." /></section>
                <section><span className="project-details-section-label">Responsive design</span><RichProjectContent content={responsiveDesign} fallback="No responsive design notes have been added yet." /></section>
                <section><span className="project-details-section-label">Technologies used</span><RichProjectContent content={technologiesUsed} fallback="No technologies have been added yet." /></section>
              </div>

              <div className="project-details-links">
                <a target="_blank" rel="noreferrer" href={demo}><FiExternalLink /> Live demo</a>
                <a target="_blank" rel="noreferrer" href={code}><FiCode /> Client code</a>
                <a target="_blank" rel="noreferrer" href={server}><FiServer /> Server code</a>
              </div>
            </div>
          </article>
        </div>
      )}
    </>
  );
};

export default ProjectDetails;
